import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, DownloadSimple, Sparkle, ArrowsClockwise, UploadSimple,
  MagicWand, Camera, Warning, Question, CircleNotch, X, Info,
} from "@phosphor-icons/react";
import { initialResume, RESUME_SAMPLES, SAMPLE_META } from "@/lib/initialData";
import { getTemplateTypographyDefaults } from "@/lib/templateTypography";
import { downloadPdf } from "@/lib/pdf";
import { parseResumeFile, tailorResume } from "@/lib/api";
import { getPaymentConfig, createOrder, verifyPayment, openCheckout } from "@/lib/payment";
import { getLocalCurrency, getDownloadCtaLabel } from "@/lib/localCurrency";
import { trackDownload, getLimits } from "@/lib/admin";
import { isFreeTemplate, isValidTemplateId, isFullBleedTemplate } from "@/components/builder/TemplatePicker";
import BuilderAdBanner from "@/components/builder/BuilderAdBanner";
import Logo from "@/components/Logo";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ResumeForm from "@/components/builder/ResumeForm";
import SectionProgressMeter from "@/components/builder/SectionProgressMeter";
import TemplatePicker from "@/components/builder/TemplatePicker";
import { PAGE_SIZES } from "@/components/builder/ResumePreview";
import LayoutControls from "@/components/builder/LayoutControls";
import PreviewPanel from "@/components/builder/PreviewPanel";
import JdModal from "@/components/builder/JdModal";
import PrivacyAlert from "@/components/builder/PrivacyAlert";
import MobileAdvisory, { hasAckMobileAdvisory } from "@/components/builder/MobileAdvisory";
import OnboardingTour from "@/components/OnboardingTour";

const PAID_DOWNLOAD_LIMIT = 3;
const FREE_DOWNLOAD_LIMIT_FALLBACK = 3;

// Reads `?template=<id>` from the URL and returns it if it's one of the
// nine known templates. Called at mount-time so `useState` can seed the
// template + typography defaults from the onboarding choice made on
// `/get-started`. Falls back to "modern" for direct-nav users or if the
// param is missing / invalid. Returns null-safe values for SSR.
function readInitialTemplateFromUrl() {
  try {
    const p = new URLSearchParams(window.location.search).get("template");
    if (p && isValidTemplateId(p)) return p;
  } catch (_) { /* SSR / private-mode fallback */ }
  return "modern";
}

export default function Builder() {
  // Preload the "software-engineer" sample so the preview shows real content
  // the moment the user lands on /builder. First-time users can hit Reset or
  // pick a different sample from the header dropdown to clear it.
  const [resume, setResume] = useState(() => RESUME_SAMPLES["software-engineer"] || initialResume);
  // Template state is seeded from `?template=<id>` when the user arrives
  // from the `/get-started` onboarding gallery. Direct navigation to
  // `/builder` (returning users, deep-links) still defaults to "modern".
  const _initialTemplate = readInitialTemplateFromUrl();
  const [template, setTemplate] = useState(_initialTemplate);
  // Bootstrap layout + typography from the initial template's presets so the
  // opening preview already shows the template at its best. Every value is
  // still user-editable — these are just defaults + reset targets.
  const _boot = getTemplateTypographyDefaults(_initialTemplate);
  const [pageSize, setPageSize] = useState("a4");
  const [density, setDensity] = useState("comfortable");
  const [fontScale, setFontScale] = useState(_boot.fontScale);
  const [fontFamily, setFontFamily] = useState(_boot.fontFamily);
  const [descriptionFontFamily, setDescriptionFontFamily] = useState(_boot.descriptionFontFamily);
  const [lineSpacing, setLineSpacing] = useState(_boot.lineSpacing);
  const [h1FontWeight, setH1FontWeight] = useState(_boot.h1FontWeight);
  const [h2FontWeight, setH2FontWeight] = useState(_boot.h2FontWeight);
  const [bodyFontWeight, setBodyFontWeight] = useState(_boot.bodyFontWeight);
  const [h1Color, setH1Color] = useState(_boot.h1Color);
  const [h2Color, setH2Color] = useState(_boot.h2Color);
  // Per-category Work-Experience typography overrides (Job Title / Company).
  // These override the template-defined inline styles via scoped CSS injected
  // in ResumePreview. Font-size is stored as absolute pt (8-16) — independent
  // of the global font-scale so users get precise control over these two
  // high-signal headline elements. Defaults match Modern template's look.
  const [jobTitleFontSize, setJobTitleFontSize] = useState(_boot.jobTitleFontSize);
  const [jobTitleFontWeight, setJobTitleFontWeight] = useState(_boot.jobTitleFontWeight);
  const [jobTitleColor, setJobTitleColor] = useState(_boot.jobTitleColor);
  const [jobTitleFontFamily, setJobTitleFontFamily] = useState(_boot.jobTitleFontFamily);
  const [companyFontSize, setCompanyFontSize] = useState(_boot.companyFontSize);
  const [companyFontWeight, setCompanyFontWeight] = useState(_boot.companyFontWeight);
  const [companyColor, setCompanyColor] = useState(_boot.companyColor);
  const [companyFontFamily, setCompanyFontFamily] = useState(_boot.companyFontFamily);
  const [resumeNameFontSize, setResumeNameFontSize] = useState(_boot.resumeNameFontSize);
  const [resumeNameFontFamily, setResumeNameFontFamily] = useState(_boot.resumeNameFontFamily);
  const [sectionHeaderFontSize, setSectionHeaderFontSize] = useState(_boot.sectionHeaderFontSize);
  const [sectionHeaderFontFamily, setSectionHeaderFontFamily] = useState(_boot.sectionHeaderFontFamily);
  const [descriptionFontSize, setDescriptionFontSize] = useState(_boot.descriptionFontSize);
  const [descriptionFontWeight, setDescriptionFontWeight] = useState(_boot.descriptionFontWeight);
  const [descriptionColor, setDescriptionColor] = useState(_boot.descriptionColor);
  // NEW iter 43 — Contact Information + Work Experience Description as
  // first-class categories. Each carries the standard 4-field shape.
  const [contactInfoFontSize, setContactInfoFontSize] = useState(_boot.contactInfoFontSize);
  const [contactInfoFontWeight, setContactInfoFontWeight] = useState(_boot.contactInfoFontWeight);
  const [contactInfoColor, setContactInfoColor] = useState(_boot.contactInfoColor);
  const [contactInfoFontFamily, setContactInfoFontFamily] = useState(_boot.contactInfoFontFamily);
  const [workDescriptionFontSize, setWorkDescriptionFontSize] = useState(_boot.workDescriptionFontSize);
  const [workDescriptionFontWeight, setWorkDescriptionFontWeight] = useState(_boot.workDescriptionFontWeight);
  const [workDescriptionColor, setWorkDescriptionColor] = useState(_boot.workDescriptionColor);
  const [workDescriptionFontFamily, setWorkDescriptionFontFamily] = useState(_boot.workDescriptionFontFamily);
  // NEW iter 44 — Date + Location as first-class Work-Experience categories.
  const [dateFontSize, setDateFontSize] = useState(_boot.dateFontSize);
  const [dateFontWeight, setDateFontWeight] = useState(_boot.dateFontWeight);
  const [dateColor, setDateColor] = useState(_boot.dateColor);
  const [dateFontFamily, setDateFontFamily] = useState(_boot.dateFontFamily);
  const [locationFontSize, setLocationFontSize] = useState(_boot.locationFontSize);
  const [locationFontWeight, setLocationFontWeight] = useState(_boot.locationFontWeight);
  const [locationColor, setLocationColor] = useState(_boot.locationColor);
  const [locationFontFamily, setLocationFontFamily] = useState(_boot.locationFontFamily);
  const [bodyTypography, setBodyTypography] = useState(_boot.bodyTypography);
  const updateBodyCategory = (key, patch) =>
    setBodyTypography((t) => ({ ...t, [key]: { ...t[key], ...patch } }));
  // Applies a full typography preset object (from lib/templateTypography.js)
  // by fanning it out to every individual setter. Used by both the template-
  // change effect and the destructive Reset-all handler so they share the
  // same source of truth for what "defaults" means.
  const applyTypographyPreset = (preset) => {
    setFontScale(preset.fontScale);
    setLineSpacing(preset.lineSpacing);
    setFontFamily(preset.fontFamily);
    setDescriptionFontFamily(preset.descriptionFontFamily);
    setH1FontWeight(preset.h1FontWeight);
    setH2FontWeight(preset.h2FontWeight);
    setBodyFontWeight(preset.bodyFontWeight);
    setH1Color(preset.h1Color);
    setH2Color(preset.h2Color);
    setJobTitleFontSize(preset.jobTitleFontSize);
    setJobTitleFontWeight(preset.jobTitleFontWeight);
    setJobTitleColor(preset.jobTitleColor);
    setJobTitleFontFamily(preset.jobTitleFontFamily);
    setCompanyFontSize(preset.companyFontSize);
    setCompanyFontWeight(preset.companyFontWeight);
    setCompanyColor(preset.companyColor);
    setCompanyFontFamily(preset.companyFontFamily);
    setResumeNameFontSize(preset.resumeNameFontSize);
    setResumeNameFontFamily(preset.resumeNameFontFamily);
    setSectionHeaderFontSize(preset.sectionHeaderFontSize);
    setSectionHeaderFontFamily(preset.sectionHeaderFontFamily);
    setDescriptionFontSize(preset.descriptionFontSize);
    setDescriptionFontWeight(preset.descriptionFontWeight);
    setDescriptionColor(preset.descriptionColor);
    setContactInfoFontSize(preset.contactInfoFontSize);
    setContactInfoFontWeight(preset.contactInfoFontWeight);
    setContactInfoColor(preset.contactInfoColor);
    setContactInfoFontFamily(preset.contactInfoFontFamily);
    setWorkDescriptionFontSize(preset.workDescriptionFontSize);
    setWorkDescriptionFontWeight(preset.workDescriptionFontWeight);
    setWorkDescriptionColor(preset.workDescriptionColor);
    setWorkDescriptionFontFamily(preset.workDescriptionFontFamily);
    setDateFontSize(preset.dateFontSize);
    setDateFontWeight(preset.dateFontWeight);
    setDateColor(preset.dateColor);
    setDateFontFamily(preset.dateFontFamily);
    setLocationFontSize(preset.locationFontSize);
    setLocationFontWeight(preset.locationFontWeight);
    setLocationColor(preset.locationColor);
    setLocationFontFamily(preset.locationFontFamily);
    setBodyTypography(preset.bodyTypography);
  };

  // When the user picks a different template, auto-swap typography to that
  // template's tuned defaults so every template loads at its best out-of-
  // the-box. User customisations after the switch are of course preserved
  // until the next template change or Reset-all.
  const initialTemplateRef = useRef(template);
  useEffect(() => {
    if (initialTemplateRef.current === template) return; // skip first render
    initialTemplateRef.current = template;
    applyTypographyPreset(getTemplateTypographyDefaults(template));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template]);
  const [movedSections, setMovedSections] = useState([]);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [mobileView, setMobileView] = useState("edit");
  const [formTab, setFormTab] = useState("templates"); // "templates" | "editor"
  // Mobile viewports get a "fit-to-width" default so the whole A4 page is
  // visible at a glance. Desktop stays at 1.0 (native).
  const [previewZoom, setPreviewZoom] = useState(() => {
    if (typeof window === "undefined") return 1;
    const vw = window.innerWidth;
    if (vw >= 1024) return 1;   // lg+: native
    if (vw >= 640) return 0.85; // tablet: slight zoom out
    // Mobile: fit the paper to the visible preview column width. Paper is
    // 794 px native; leave ~24 px of viewport padding so the corner
    // watermark isn't clipped.
    return Math.max(0.4, Math.min(1, (vw - 24) / 794));
  });
  const [resumeImported, setResumeImported] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [jdOpen, setJdOpen] = useState(false);
  const [jd, setJd] = useState("");
  const [tailoring, setTailoring] = useState(false);
  const [tailorNotes, setTailorNotes] = useState(null);  const [payCfg, setPayCfg] = useState({ enabled: false, amount_usd: 1 });
  const [localCurrency, setLocalCurrency] = useState(null);
  const [autoFitting, setAutoFitting] = useState(false);
  const [showPrivacyAlert, setShowPrivacyAlert] = useState(false);
  const [confirmPayOpen, setConfirmPayOpen] = useState(false);
  // Free-download quota. `limit` is fetched from `/api/config/limits`
  // (configurable via FREE_DOWNLOAD_LIMIT env var); count is persisted
  // per-session so refreshes don't reset the meter.
  const [confirmFreeOpen, setConfirmFreeOpen] = useState(false);
  const [freeDownloadLimit, setFreeDownloadLimit] = useState(FREE_DOWNLOAD_LIMIT_FALLBACK);
  const [freeDownloadCount, setFreeDownloadCount] = useState(() => {
    try { return parseInt(sessionStorage.getItem("myresumedesign_free_downloads") || "0", 10) || 0; }
    catch (e) { return 0; }
  });
  const [resetAllOpen, setResetAllOpen] = useState(false);
  // Welcome dialog — opens sequentially AFTER the user acknowledges the
  // Privacy/Terms alert. Guarded by sessionStorage so returning users
  // editing the same session don't see it again.
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  // Inline onboarding callout shown once after "Create New Résumé" —
  // dismissible via X. Guarded so it appears at most once per session.
  const [createGuideVisible, setCreateGuideVisible] = useState(false);
  const [mobileAdvisoryOpen, setMobileAdvisoryOpen] = useState(false);
  const [paidClean, setPaidClean] = useState(() => {
    try { return sessionStorage.getItem("myresumedesign_paid_clean") === "1"; }
    catch (e) { return false; }
  });
  const [sessionId] = useState(() => {
    try {
      let sid = sessionStorage.getItem("myresumedesign_session_id");
      if (!sid) {
        sid = "s-" + (crypto?.randomUUID?.() || Math.random().toString(36).slice(2) + Date.now().toString(36));
        sessionStorage.setItem("myresumedesign_session_id", sid);
      }
      return sid;
    } catch (e) {
      return "s-" + Math.random().toString(36).slice(2);
    }
  });
  const [paidDownloadCount, setPaidDownloadCount] = useState(() => {
    try { return parseInt(sessionStorage.getItem("myresumedesign_paid_downloads") || "0", 10) || 0; }
    catch (e) { return 0; }
  });
  const [lastPayment, setLastPayment] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("myresumedesign_last_payment") || "null"); }
    catch (e) { return null; }
  });

  const paidDownloadsLeft = Math.max(0, PAID_DOWNLOAD_LIMIT - paidDownloadCount);
  const paidQuotaExhausted = paidClean && paidDownloadCount >= PAID_DOWNLOAD_LIMIT;
  // Free-download quota (only applies when the current template is free).
  const freeDownloadsLeft = Math.max(0, freeDownloadLimit - freeDownloadCount);
  const freeQuotaExhausted = freeDownloadCount >= freeDownloadLimit;
  const hasContent = Boolean(resume.personal?.fullName?.trim());
  const isFreeTemplateNow = isFreeTemplate(template);
  const downloadBlocked = downloading || paidQuotaExhausted || !hasContent || (isFreeTemplateNow && freeQuotaExhausted);
  // JD Match gating: users can tailor to a JD only after either importing an
  // existing résumé OR filling personal details + at least one work experience.
  // Without a base résumé the AI has nothing to rewrite, which was creating
  // confusing "empty resume tailored" UX.
  const hasPersonalDetails = Boolean(
    resume.personal?.fullName?.trim() &&
      (resume.personal?.email?.trim() || resume.personal?.phone?.trim())
  );
  const hasAnyExperience = (resume.experience || []).some(
    (e) => e.role?.trim() && e.company?.trim()
  );
  const canUseJd = resumeImported || (hasPersonalDetails && hasAnyExperience);
  const discount = payCfg.discount || null;
  // Localized primary CTA: "Download Resume for ₹99" (India) or
  // "Download Resume for USD $1" (everyone else). Falls back to the second
  // string until IP detection resolves so the button never renders blank.
  // Free-template gating (iter 46): Modern + ATS Plain are free-to-download
  // — no Razorpay checkout, no watermark, and the CTA copy simply reads
  // "Download Resume" without the price suffix. Every other template stays
  // on the existing paid flow.
  const isFree = isFreeTemplate(template);
  const effectivePayEnabled = payCfg.enabled && !isFree;
  const downloadCtaLabel = isFree ? "Download Resume" : getDownloadCtaLabel(localCurrency, discount);

  const previewRef = useRef(null);
  const fileRef = useRef(null);
  const photoRef = useRef(null);

  useEffect(() => {
    getPaymentConfig().then(setPayCfg).catch(() => {});
    getLocalCurrency().then(setLocalCurrency).catch(() => {});
    // Fetch the configurable free-download limit (defaults to fallback if
    // the endpoint is unreachable — never blocks the builder from loading).
    getLimits()
      .then((cfg) => {
        if (cfg?.free_download_limit != null) setFreeDownloadLimit(cfg.free_download_limit);
      })
      .catch(() => {});

    // Onboarding entry: users arriving from `/get-started` carry
    //   ?template=<id>&intent=<upload|create>
    // The template was already applied by the `useState` initializer above.
    // The intent auto-dispatches the corresponding builder action here so
    // users land straight in the flow they picked — no extra dialog to
    // click through.
    //
    // Legacy: Landing hero used to append `?welcome=1` which forced the
    // Welcome dialog on. That entry point is now routed through
    // `/get-started`, but we keep support for `?welcome=1` so any
    // bookmarked or deep-linked URLs still behave. Direct `/builder`
    // navigation retains the original Welcome-dialog onboarding.
    let forceWelcome = false;
    let onboardingIntent = null;
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("welcome") === "1") {
        forceWelcome = true;
        sessionStorage.removeItem("mrd_welcome_ack");
      }
      const rawIntent = params.get("intent");
      if (rawIntent === "upload" || rawIntent === "create") {
        onboardingIntent = rawIntent;
        // Welcome dialog was already handled on `/get-started` — mark ack.
        try { sessionStorage.setItem("mrd_welcome_ack", "1"); } catch (_) { /* ignore */ }
      }
      // Strip params from the URL so refreshes / share links don't loop
      // the onboarding dispatch or re-force the Welcome dialog.
      if (params.get("welcome") || params.get("template") || params.get("intent")) {
        params.delete("welcome");
        params.delete("template");
        params.delete("intent");
        const clean = window.location.pathname + (params.toString() ? `?${params}` : "");
        window.history.replaceState({}, "", clean);
      }
    } catch (_) { /* SSR / private-mode fallback */ }

    try {
      const ack = sessionStorage.getItem("myresumedesign_privacy_ack");
      if (!ack && !onboardingIntent) {
        // Direct /builder nav with no onboarding — show the T&C gate.
        setShowPrivacyAlert(true);
      } else if (forceWelcome && !onboardingIntent) {
        // Terms already done — jump straight to Welcome dialog (legacy path).
        setTimeout(() => setWelcomeOpen(true), 100);
      } else if (onboardingIntent === "upload") {
        // /get-started upload path — flip to the Editor tab so the file
        // input is visible/reachable, then trigger the picker.
        setFormTab("editor");
        setTimeout(() => fileRef.current?.click(), 250);
      } else if (onboardingIntent === "create") {
        // /get-started create path — run the same reset+focus flow the
        // in-Builder Welcome dialog's "Create" button uses so behaviour is
        // identical whether the user came via onboarding or the legacy
        // dialog. Deferred to the next tick so setState calls in the
        // effect settle first.
        setTimeout(() => handleWelcomeCreateNew(), 100);
      }
    } catch (e) {
      setShowPrivacyAlert(true);
    }
  }, []);

  const dismissPrivacyAlert = () => {
    try { sessionStorage.setItem("myresumedesign_privacy_ack", "1"); } catch (e) { /* ignore */ }
    setShowPrivacyAlert(false);
    // Step 2 of onboarding: show the Welcome dialog once per session
    // (Upload existing résumé vs. Start from scratch). Only if it hasn't
    // already been acknowledged in this session.
    try {
      if (!sessionStorage.getItem("mrd_welcome_ack")) {
        setTimeout(() => setWelcomeOpen(true), 200);
        return;
      }
    } catch (_) { /* SSR / private-mode fallback */ }
    // Welcome already ack'd — fall through to the mobile advisory chain.
    if (!hasAckMobileAdvisory()) {
      setTimeout(() => setMobileAdvisoryOpen(true), 250);
    }
  };

  // Chained from the Welcome dialog close. Marks welcome as ack'd and
  // continues to the mobile advisory (mobile-only, no-op on desktop).
  // The Product Tour is intentionally NOT auto-launched here — users
  // can start it any time via the tour button in the header.
  const dismissWelcomeDialog = () => {
    try { sessionStorage.setItem("mrd_welcome_ack", "1"); } catch (_) { /* ignore */ }
    setWelcomeOpen(false);
    if (!hasAckMobileAdvisory()) {
      setTimeout(() => setMobileAdvisoryOpen(true), 250);
    }
  };

  // "Create New Résumé" path from the Welcome dialog. Clears any preview
  // sample, switches the left panel from Templates to Editor, scrolls the
  // Personal Details section into view, focuses the Full Name input, and
  // reveals a one-time dismissible guidance callout at the top of the
  // editor. Guarded by sessionStorage so it only shows once per session.
  const handleWelcomeCreateNew = () => {
    setResume(initialResume);
    setFormTab("editor");
    setResumeImported(false);
    setMobileView("edit");
    dismissWelcomeDialog();
    try {
      if (!sessionStorage.getItem("mrd_create_guide_shown")) {
        setCreateGuideVisible(true);
        sessionStorage.setItem("mrd_create_guide_shown", "1");
      }
    } catch (_) { /* ignore */ }
    // Give React a beat to render the Editor tab, then scroll the form
    // column so the Personal Details HEADING (not just the first field)
    // sits just below the sticky Templates/Editor tabs bar, then focus
    // the Full Name input.
    setTimeout(() => {
      const container = document.querySelector('[data-testid="form-column"]');
      const section = document.querySelector('[data-testid="section-personal"]');
      if (container && section) {
        const cRect = container.getBoundingClientRect();
        const sRect = section.getBoundingClientRect();
        // Sticky tabs bar is ~48px tall; leave a comfortable 12px gap
        // beneath it so the heading is clearly visible, not hugging the bar.
        const STICKY_OFFSET = 60;
        container.scrollTop += sRect.top - cRect.top - STICKY_OFFSET;
      }
      setTimeout(() => {
        const input = document.querySelector('[data-testid="input-fullName"]');
        if (input) {
          input.focus({ preventScroll: true });
        }
      }, 450);
    }, 120);
  };

  const dismissMobileAdvisory = () => {
    setMobileAdvisoryOpen(false);
    // Product Tour intentionally not auto-launched — user opens it manually
    // from the header tour button.
  };

  const runDownload = async (opts = {}) => {
    setDownloading(true);
    try {
      const name = resume.personal.fullName?.trim().replace(/\s+/g, "_") || "resume";
      const paper = previewRef.current?.querySelector(".paper") || previewRef.current;
      await downloadPdf(paper, `${name}.pdf`, PAGE_SIZES[pageSize].jsFormat, isFullBleedTemplate(template));
      const kind = opts.kind || (paidClean ? "paid" : "free");
      trackDownload({
        kind,
        template,
        page_size: pageSize,
        font_scale: fontScale,
        density,
        session_id: sessionId,
        order_id: opts.order_id || lastPayment?.order_id || null,
        payment_id: opts.payment_id || lastPayment?.payment_id || null,
      });
      if (kind === "paid") {
        const next = paidDownloadCount + 1;
        setPaidDownloadCount(next);
        try { sessionStorage.setItem("myresumedesign_paid_downloads", String(next)); } catch (e) { /* ignore */ }
      }
      toast.success("PDF downloaded");
    } catch (e) {
      // Surface the backend detail (e.g. "PDF service is initialising")
      // so the user gets an actionable message instead of a generic error.
      const detail = e?.response?.data?.detail
        || (typeof e?.response?.data === "string" ? e.response.data : null)
        || e?.message
        || "Could not generate PDF";
      console.error("PDF download failed:", e);
      toast.error(String(detail).slice(0, 240));
    } finally {
      setDownloading(false);
    }
  };

  const handlePaidDownload = async () => {
    if (!previewRef.current) return;
    if (!effectivePayEnabled) return runDownload();
    setDownloading(true);
    try {
      const order = await createOrder();
      const resp = await openCheckout({
        orderId: order.order_id,
        keyId: order.key_id,
        amount: order.amount,
        prefill: {
          name: resume.personal.fullName,
          email: resume.personal.email,
          contact: resume.personal.phone,
        },
      });
      await verifyPayment({
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
      });
      setPaidClean(true);
      const payInfo = { order_id: order.order_id, payment_id: resp.razorpay_payment_id };
      setLastPayment(payInfo);
      setPaidDownloadCount(0);
      try {
        sessionStorage.setItem("myresumedesign_paid_clean", "1");
        sessionStorage.setItem("myresumedesign_last_payment", JSON.stringify(payInfo));
        sessionStorage.setItem("myresumedesign_paid_downloads", "0");
      } catch (e) { /* ignore */ }
      toast.success(`Payment received · ${PAID_DOWNLOAD_LIMIT} clean downloads unlocked`);
      await new Promise((r) => setTimeout(() => requestAnimationFrame(r), 100));
      await runDownload({ kind: "paid", order_id: payInfo.order_id, payment_id: payInfo.payment_id });
    } catch (e) {
      if (e?.code === "cancelled") {
        toast.info("Payment cancelled");
      } else {
        if (process.env.NODE_ENV === "development") console.error("Payment error:", e, e?.detail);
        toast.error(e.message || "Payment failed");
      }
    } finally {
      setDownloading(false);
    }
  };

  // Runs a free-tier download (Modern / ATS Plain). Confirms via dialog,
  // then decrements the per-session quota. Persists count so refreshes
  // don't reset the meter — matches the paid quota's UX contract.
  const runFreeDownload = async () => {
    setConfirmFreeOpen(false);
    if (freeQuotaExhausted) {
      toast.error("Free download limit reached for this session");
      return;
    }
    const next = freeDownloadCount + 1;
    setFreeDownloadCount(next);
    try { sessionStorage.setItem("myresumedesign_free_downloads", String(next)); } catch (e) { /* ignore */ }
    // Wait for the dialog close state to flush + the DOM to unmount the
    // AlertDialog portal before triggering the browser print dialog.
    // Without this, `window.print()` snapshots the page while the dialog
    // is still visible and captures it into the exported PDF.
    await new Promise((resolve) => setTimeout(resolve, 120));
    await runDownload({ kind: "free" });
  };

  const handleDownload = () => {
    if (paidClean && !paidQuotaExhausted) return runDownload();
    if (effectivePayEnabled) return setConfirmPayOpen(true);
    // Free-tier template — gate every download behind the confirmation
    // dialog so users see the remaining-count warning + limit note.
    if (isFree) {
      if (freeQuotaExhausted) {
        toast.error("Free download limit reached for this session");
        return;
      }
      return setConfirmFreeOpen(true);
    }
    return runDownload();
  };

  // Called from PreviewPanel's paywall/repay CTAs — always gated behind the
  // confirmation dialog whenever a real payment is about to be initiated.
  const requestPaidDownload = () => {
    if (effectivePayEnabled) setConfirmPayOpen(true);
    else handlePaidDownload();
  };

  const confirmAndPay = () => {
    setConfirmPayOpen(false);
    handlePaidDownload();
  };

  // Reset everything to defaults — typography (all 20+ knobs) picks up
  // the CURRENT template's tastefully-tuned preset (see
  // lib/templateTypography.js) so hitting reset on Two-Column keeps the
  // sidebar white while Modern goes fully black, etc. Résumé content is
  // still swapped for the built-in software-engineer sample. Destructive,
  // so guarded by an AlertDialog confirmation.
  const handleResetAll = () => {
    // Layout — non-typography knobs stay simple/global.
    setPageSize("a4");
    setDensity("comfortable");
    // Typography — apply the current template's preset in one shot.
    applyTypographyPreset(getTemplateTypographyDefaults(template));
    // Content — back to the default software-engineer sample so the preview
    // still has demo data (matches the initial Builder load state).
    setResume(RESUME_SAMPLES["software-engineer"] || initialResume);
    // Reset overflow warning banner state as well.
    setMovedSections([]);
    setHasOverflow(false);
    setResetAllOpen(false);
    toast.success("Everything reset · typography and content back to defaults");
  };

  const handleAutoFit = async () => {    if (autoFitting) return;
    setAutoFitting(true);
    try {
      // Progressive fit: iterate through (density, font-size) combinations
      // from lightest touch to tightest. Each combo is evaluated
      // independently — as soon as one settles pagination with zero
      // page-break spacers, we lock it in. This favours line-spacing +
      // subtle size drops over aggressive shrinking so the résumé stays
      // legible and balanced per page.
      const wait = () => new Promise((r) =>
        setTimeout(() => requestAnimationFrame(() => requestAnimationFrame(r)), 80)
      );
      const fits = () => {
        const paper = previewRef.current?.querySelector(".paper");
        if (!paper) return true;
        return paper.querySelectorAll("[data-pagebreak-spacer]").length === 0;
      };
      // Ordered from least-invasive → most-aggressive.
      const trials = [
        { density: "comfortable", scale: 1.0 },
        { density: "comfortable", scale: 0.9 },
        { density: "compact",     scale: 1.0 },
        { density: "compact",     scale: 0.9 },
        { density: "compact",     scale: 0.85 },
        { density: "compact",     scale: 0.8 },
      ];
      for (const t of trials) {
        setDensity(t.density);
        setFontScale(t.scale);
        await wait();
        if (fits()) {
          toast.success(`Fit to one page · ${Math.round(t.scale * 10)}pt · ${t.density}`);
          return;
        }
      }
      toast.info("Still spans multiple pages at 8pt · compact — content preserved across pages");
    } finally {
      setAutoFitting(false);
    }
  };

  const onUploadResume = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (uploading) return; // guard: prevent parallel imports
    setUploading(true);
    try {
      const parsed = await parseResumeFile(f);
      // Populate the editor state — this covers the "AI data extraction +
      // data validation + populating editor" step. `setResume` batches into
      // a single React commit so pagination re-runs once, not per section.
      setResume((r) => ({
        ...initialResume,
        ...parsed,
        photo: r.photo,
        personal: { ...initialResume.personal, ...(parsed.personal || {}) },
      }));
      setResumeImported(true);
      toast.success("Resume imported successfully · Your résumé is ready to edit");
    } catch (err) {
      const status = err.response?.status;
      if (status === 504 || err.code === "ECONNABORTED") {
        toast.error("Parsing took too long. Try a shorter résumé (under 3 pages) or retry.");
      } else {
        toast.error(err.response?.data?.detail || "Import failed — please try again");
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onUploadPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) {
      toast.error("Photo must be under 2MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setResume((r) => ({ ...r, photo: reader.result }));
      toast.success("Photo added");
    };
    reader.readAsDataURL(f);
    if (photoRef.current) photoRef.current.value = "";
  };

  const runTailor = async () => {
    if (!jd.trim()) return toast.error("Paste a job description first");
    setTailoring(true);
    try {
      const res = await tailorResume({ resume, jd });
      setResume((r) => ({ ...r, ...res.resume, photo: r.photo }));
      if (res.recommended_template) setTemplate(res.recommended_template);
      setTailorNotes({ notes: res.match_notes, skills: res.suggested_skills || [] });
      toast.success("Resume tailored");
      setJdOpen(false);
    } catch (e) {
      const status = e.response?.status;
      if (status === 504 || e.code === "ECONNABORTED") {
        toast.error("Match JD took too long. Try a shorter job description or retry.");
      } else {
        toast.error(e.response?.data?.detail || "Tailor failed");
      }
    } finally {
      setTailoring(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-neutral-950 font-sans" data-testid="builder-page">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/10">
        <div className="px-4 lg:px-6 py-2 sm:py-0 sm:h-14 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3">
          <div className="flex items-center justify-between sm:justify-start gap-4 min-w-0">
            <div className="flex items-center gap-4 min-w-0">
              <Link to="/" className="flex items-center gap-2 text-sm text-neutral-700 hover:text-black" data-testid="back-home">
                <ArrowLeft size={16} weight="bold" />
                <span className="hidden sm:inline">Home</span>
              </Link>
              <div className="h-4 w-px bg-black/20" />
              <Logo />
            </div>
            {/* Tour + Help sit next to the logo on mobile only — keeps the
                second (action) row focused on Import / Match JD / Download. */}
            <div className="flex sm:hidden items-center gap-1.5 shrink-0">
              <button
                onClick={() => setTourOpen(true)}
                className="inline-flex items-center justify-center w-8 h-8 border border-black/10 hover:bg-neutral-100 transition-colors text-neutral-700"
                title="Take a quick product tour"
                data-testid="builder-tour-button-mobile"
                aria-label="Take a tour"
              >
                <Sparkle size={14} weight="fill" className="text-[#FF2A00]" />
              </button>
              <Link
                to="/help"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center w-8 h-8 border border-black/10 hover:bg-neutral-100 transition-colors text-neutral-700"
                title="Help & guide"
                data-testid="builder-help-link-mobile"
              >
                <Question size={14} weight="bold" />
              </Link>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap justify-end">
            <input ref={fileRef} type="file" accept=".pdf,.docx" onChange={onUploadResume} className="hidden" data-testid="upload-resume-input" />
            <button
              onClick={() => !uploading && fileRef.current?.click()}
              disabled={uploading}
              className={`inline-flex items-center gap-1.5 sm:gap-2 text-xs font-mono uppercase tracking-widest px-2.5 sm:px-3 py-2 border transition-colors ${
                uploading
                  ? "border-[#2400FF]/40 bg-[#2400FF]/[0.04] text-[#2400FF] cursor-wait"
                  : "border-black/10 hover:bg-neutral-100"
              }`}
              data-testid="upload-resume-button"
              aria-busy={uploading}
              title={uploading ? "Parsing résumé — please wait" : "Upload an existing résumé (PDF/DOCX)"}
              aria-label={uploading ? "Parsing résumé" : "Upload résumé"}
            >
              {uploading ? (
                <CircleNotch
                  size={14}
                  weight="bold"
                  className="animate-spin"
                  data-testid="upload-resume-spinner"
                />
              ) : (
                <UploadSimple size={14} weight="bold" />
              )}
              <span className="hidden sm:inline" data-testid="upload-resume-button-label">
                {uploading ? "Parsing…" : "Upload"}
              </span>
            </button>
            <button
              onClick={() => {
                if (!canUseJd) {
                  toast.info(
                    "Add your personal details and at least one work experience — or import a résumé — before matching a JD.",
                    { duration: 5000 }
                  );
                  return;
                }
                setJdOpen(true);
              }}
              disabled={!canUseJd}
              className={`inline-flex items-center gap-1.5 sm:gap-2 text-xs font-mono uppercase tracking-widest px-2.5 sm:px-3 py-2 border transition-colors ${
                canUseJd
                  ? "border-[#FF2A00]/30 text-[#FF2A00] hover:bg-[#FF2A00]/5"
                  : "border-black/10 text-neutral-400 cursor-not-allowed"
              }`}
              data-testid="open-jd-button"
              title={
                canUseJd
                  ? "Match résumé to a job description"
                  : "Import a résumé or fill personal details + one work experience to unlock JD matching"
              }
              aria-label="Match résumé to a job description"
            >
              <MagicWand size={14} weight="bold" />
              <span className="hidden sm:inline">Match JD</span>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="inline-flex items-center gap-1.5 text-[10px] sm:text-xs font-mono uppercase tracking-widest px-2 sm:px-3 py-2 border border-black/10 hover:bg-neutral-100 transition-colors whitespace-nowrap"
                  data-testid="load-sample-button"
                  aria-label="Load sample resume"
                >
                  Load sample
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72" data-testid="load-sample-menu">
                <DropdownMenuLabel className="text-[10px] font-mono uppercase tracking-widest text-neutral-500">
                  Pick a role-based sample
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {SAMPLE_META.map((s) => (
                  <DropdownMenuItem
                    key={s.id}
                    onSelect={() => {
                      setResume(RESUME_SAMPLES[s.id]);
                      toast.success(`Loaded: ${s.label}`);
                    }}
                    className="flex flex-col items-start gap-0.5 py-2 cursor-pointer"
                    data-testid={`load-sample-${s.id}`}
                  >
                    <span className="font-medium text-sm">{s.label}</span>
                    <span className="text-[11px] text-neutral-500 leading-snug">{s.description}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button onClick={() => { if (window.confirm("Clear all?")) setResume(initialResume); }} className="hidden md:inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest px-3 py-2 border border-black/10 hover:bg-neutral-100 transition-colors" data-testid="reset-button">
              <ArrowsClockwise size={14} weight="bold" /> Reset
            </button>
            <button
              onClick={handleDownload}
              disabled={downloadBlocked}
              className="btn-primary text-xs px-4 py-2 relative"
              data-testid="download-pdf-button"
            >
              {discount && !paidClean && effectivePayEnabled && hasContent && !downloading && !paidQuotaExhausted && (
                <span
                  className="absolute -top-2 -right-2 bg-[#FF2A00] text-white text-[9px] font-mono font-bold px-1.5 py-0.5 tracking-wider"
                  data-testid="discount-badge-header"
                >
                  {discount.percent}% OFF
                </span>
              )}
              <DownloadSimple size={16} weight="bold" />
              {downloading
                ? "Preparing…"
                : !hasContent
                  ? "Add your name to download"
                  : paidQuotaExhausted
                    ? "Limit reached"
                    : isFree && freeQuotaExhausted
                      ? "Free limit reached"
                      : isFree
                        ? `Download PDF · Free · ${freeDownloadsLeft}/${freeDownloadLimit} left`
                        : paidClean
                          ? `Download PDF · ${paidDownloadsLeft}/${PAID_DOWNLOAD_LIMIT} left`
                          : effectivePayEnabled
                            ? (
                              discount ? (
                                <span className="inline-flex items-center gap-1.5">
                                  <span className="line-through opacity-60">USD ${payCfg.amount_usd}</span>
                                  <span>{downloadCtaLabel}</span>
                                </span>
                              ) : downloadCtaLabel
                            )
                            : downloadCtaLabel}
            </button>
            <button
              onClick={() => setTourOpen(true)}
              className="hidden sm:inline-flex items-center justify-center w-9 h-9 border border-black/10 hover:bg-neutral-100 transition-colors text-neutral-700"
              title="Take a quick product tour"
              data-testid="builder-tour-button"
              aria-label="Take a tour"
            >
              <Sparkle size={16} weight="fill" className="text-[#FF2A00]" />
            </button>
            <Link
              to="/help"
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center justify-center w-9 h-9 border border-black/10 hover:bg-neutral-100 transition-colors text-neutral-700"
              title="Help &amp; guide"
              data-testid="builder-help-link"
            >
              <Question size={16} weight="bold" />
            </Link>
          </div>
        </div>

        <div className="lg:hidden border-t border-black/10 flex">
          <button onClick={() => setMobileView("edit")} className={`flex-1 py-2.5 text-xs font-mono uppercase tracking-widest ${mobileView === "edit" ? "bg-neutral-950 text-white" : "text-neutral-600"}`} data-testid="mobile-tab-edit">Edit</button>
          <button onClick={() => setMobileView("preview")} className={`flex-1 py-2.5 text-xs font-mono uppercase tracking-widest ${mobileView === "preview" ? "bg-neutral-950 text-white" : "text-neutral-600"}`} data-testid="mobile-tab-preview">Preview</button>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <section className={`${mobileView === "edit" ? "block" : "hidden"} lg:block border-r border-black/10 h-[calc(100vh-56px)] overflow-y-auto form-scroll`} data-testid="form-column">
          {/* Templates / Editor tab switcher — Templates first so first-time
              visitors immediately see the layout gallery, then flip to Editor
              to fill in details. */}
          <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-black/10 flex" data-testid="form-tabs">
            <button
              type="button"
              onClick={() => setFormTab("templates")}
              className={`flex-1 py-3 text-xs font-mono uppercase tracking-widest transition-colors ${
                formTab === "templates"
                  ? "bg-neutral-950 text-white"
                  : "text-neutral-600 hover:bg-neutral-100"
              }`}
              data-testid="form-tab-templates"
              aria-pressed={formTab === "templates"}
            >
              Templates
            </button>
            <button
              type="button"
              onClick={() => setFormTab("editor")}
              className={`flex-1 py-3 text-xs font-mono uppercase tracking-widest transition-colors ${
                formTab === "editor"
                  ? "bg-neutral-950 text-white"
                  : "text-neutral-600 hover:bg-neutral-100"
              }`}
              data-testid="form-tab-editor"
              aria-pressed={formTab === "editor"}
            >
              Editor
            </button>
          </div>

          <div className="p-5 lg:p-8">
            {formTab === "templates" ? (
              <TemplatePicker value={template} onChange={setTemplate} />
            ) : (
              <>
                <SectionProgressMeter resume={resume} />
                <LayoutControls
                  pageSize={pageSize}
                  setPageSize={setPageSize}
                  density={density}
                  setDensity={setDensity}
                  fontScale={fontScale}
                  setFontScale={setFontScale}
                  fontFamily={fontFamily}
                  setFontFamily={setFontFamily}
                  descriptionFontFamily={descriptionFontFamily}
                  setDescriptionFontFamily={setDescriptionFontFamily}
                  lineSpacing={lineSpacing}
                  setLineSpacing={setLineSpacing}
                  h1FontWeight={h1FontWeight}
                  setH1FontWeight={setH1FontWeight}
                  h2FontWeight={h2FontWeight}
                  setH2FontWeight={setH2FontWeight}
                  bodyFontWeight={bodyFontWeight}
                  setBodyFontWeight={setBodyFontWeight}
                  h1Color={h1Color}
                  setH1Color={setH1Color}
                  h2Color={h2Color}
                  setH2Color={setH2Color}
                  jobTitleFontSize={jobTitleFontSize}
                  setJobTitleFontSize={setJobTitleFontSize}
                  jobTitleFontWeight={jobTitleFontWeight}
                  setJobTitleFontWeight={setJobTitleFontWeight}
                  jobTitleColor={jobTitleColor}
                  setJobTitleColor={setJobTitleColor}
                  jobTitleFontFamily={jobTitleFontFamily}
                  setJobTitleFontFamily={setJobTitleFontFamily}
                  companyFontSize={companyFontSize}
                  setCompanyFontSize={setCompanyFontSize}
                  companyFontWeight={companyFontWeight}
                  setCompanyFontWeight={setCompanyFontWeight}
                  companyColor={companyColor}
                  setCompanyColor={setCompanyColor}
                  companyFontFamily={companyFontFamily}
                  setCompanyFontFamily={setCompanyFontFamily}
                  resumeNameFontSize={resumeNameFontSize}
                  setResumeNameFontSize={setResumeNameFontSize}
                  resumeNameFontFamily={resumeNameFontFamily}
                  setResumeNameFontFamily={setResumeNameFontFamily}
                  sectionHeaderFontSize={sectionHeaderFontSize}
                  setSectionHeaderFontSize={setSectionHeaderFontSize}
                  sectionHeaderFontFamily={sectionHeaderFontFamily}
                  setSectionHeaderFontFamily={setSectionHeaderFontFamily}
                  descriptionFontSize={descriptionFontSize}
                  setDescriptionFontSize={setDescriptionFontSize}
                  descriptionFontWeight={descriptionFontWeight}
                  setDescriptionFontWeight={setDescriptionFontWeight}
                  descriptionColor={descriptionColor}
                  setDescriptionColor={setDescriptionColor}
                  contactInfoFontSize={contactInfoFontSize}
                  setContactInfoFontSize={setContactInfoFontSize}
                  contactInfoFontWeight={contactInfoFontWeight}
                  setContactInfoFontWeight={setContactInfoFontWeight}
                  contactInfoColor={contactInfoColor}
                  setContactInfoColor={setContactInfoColor}
                  contactInfoFontFamily={contactInfoFontFamily}
                  setContactInfoFontFamily={setContactInfoFontFamily}
                  workDescriptionFontSize={workDescriptionFontSize}
                  setWorkDescriptionFontSize={setWorkDescriptionFontSize}
                  workDescriptionFontWeight={workDescriptionFontWeight}
                  setWorkDescriptionFontWeight={setWorkDescriptionFontWeight}
                  workDescriptionColor={workDescriptionColor}
                  setWorkDescriptionColor={setWorkDescriptionColor}
                  workDescriptionFontFamily={workDescriptionFontFamily}
                  setWorkDescriptionFontFamily={setWorkDescriptionFontFamily}
                  dateFontSize={dateFontSize}
                  setDateFontSize={setDateFontSize}
                  dateFontWeight={dateFontWeight}
                  setDateFontWeight={setDateFontWeight}
                  dateColor={dateColor}
                  setDateColor={setDateColor}
                  dateFontFamily={dateFontFamily}
                  setDateFontFamily={setDateFontFamily}
                  locationFontSize={locationFontSize}
                  setLocationFontSize={setLocationFontSize}
                  locationFontWeight={locationFontWeight}
                  setLocationFontWeight={setLocationFontWeight}
                  locationColor={locationColor}
                  setLocationColor={setLocationColor}
                  locationFontFamily={locationFontFamily}
                  setLocationFontFamily={setLocationFontFamily}
                  bodyTypography={bodyTypography}
                  updateBodyCategory={updateBodyCategory}
                  autoFitting={autoFitting}
                  onAutoFit={handleAutoFit}
                  onResetAll={() => setResetAllOpen(true)}
                />

                {(movedSections.length > 0 || hasOverflow) && (
                  <div className="mt-5 border border-[#FF2A00]/30 bg-[#FF2A00]/[0.04] p-3 flex items-start gap-2" data-testid="moved-sections-warning">
                    <Warning size={16} weight="fill" className="text-[#FF2A00] shrink-0 mt-0.5" />
                    <div className="text-xs text-neutral-800 leading-relaxed">
                      {movedSections.length > 0 && (
                        <>
                          <b>Moved to next page:</b> {movedSections.join(", ")}
                          <div className="text-neutral-500 mt-0.5">Kept intact to avoid mid-section breaks — only Work Experience descriptions can flow across pages.</div>
                        </>
                      )}
                      {hasOverflow && (
                        <div className="mt-2 flex items-start gap-1.5" data-testid="overflow-past-page-3-warning">
                          <span className="inline-block px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest bg-[#FF2A00] text-white shrink-0">Overflow</span>
                          <div>
                            <b>Content exceeds 3 pages.</b>{" "}
                            <span className="text-neutral-600">Anything past page 3 will be clipped in the PDF. Trim bullets, switch to <b>Compact</b> density, or drop the font size to 8-9 pt to bring it back within 3 pages.</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {(template === "two-column" || template === "photo-header") && (
                  <div className="mt-6 border border-black/10 p-3 flex items-center gap-3" data-testid="photo-uploader">
                    <div className="w-14 h-14 rounded-full bg-neutral-100 border border-black/10 overflow-hidden flex items-center justify-center">
                      {resume.photo ? <img src={resume.photo} alt="" className="w-full h-full object-cover" /> : <Camera size={18} className="text-neutral-400" />}
                    </div>
                    <div className="flex-1">
                      <div className="label-mono">Profile photo</div>
                      <div className="text-xs text-neutral-500 mt-1">JPG/PNG · under 2MB · session only</div>
                    </div>
                    <input ref={photoRef} type="file" accept="image/*" onChange={onUploadPhoto} className="hidden" data-testid="photo-input" />
                    <div className="flex flex-col gap-1">
                      <button onClick={() => photoRef.current?.click()} className="btn-secondary text-[10px] px-2 py-1" data-testid="photo-upload-button">{resume.photo ? "Change" : "Upload"}</button>
                      {resume.photo && <button onClick={() => setResume((r) => ({ ...r, photo: "" }))} className="text-[10px] font-mono uppercase text-neutral-400 hover:text-[#FF2A00]" data-testid="photo-remove-button">Remove</button>}
                    </div>
                  </div>
                )}

                {tailorNotes && (
                  <div className="mt-6 border border-[#FF2A00]/30 bg-[#FF2A00]/[0.03] p-3" data-testid="tailor-notes">
                    <div className="label-mono text-[#FF2A00] mb-1">JD match notes</div>
                    <div className="text-xs text-neutral-700">{tailorNotes.notes}</div>
                    {tailorNotes.skills.length > 0 && (
                      <div className="mt-2">
                        <div className="text-[10px] font-mono uppercase text-neutral-500 mb-1">Suggested skills (review before adding):</div>
                        <div className="flex flex-wrap gap-1">
                          {tailorNotes.skills.map((s, i) => (
                            <button key={`${s}-${i}`} onClick={() => setResume((r) => ({ ...r, skills: [...new Set([...(r.skills || []), s])] }))} className="text-[10px] px-2 py-0.5 border border-[#FF2A00]/40 hover:bg-[#FF2A00] hover:text-white" data-testid={`suggested-skill-${i}`}>+ {s}</button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="h-8" />
                {createGuideVisible && (
                  <div
                    className="mb-6 border border-[#2400FF]/25 bg-[#2400FF]/[0.04] p-4 flex items-start gap-3"
                    role="status"
                    aria-live="polite"
                    data-testid="create-guide-callout"
                  >
                    <Info size={18} weight="duotone" className="text-[#2400FF] shrink-0 mt-0.5" />
                    <div className="flex-1 text-sm leading-relaxed">
                      <div className="font-heading font-semibold text-neutral-950 text-base tracking-tight mb-1">
                        You're ready to build your résumé
                      </div>
                      <div className="text-neutral-700">
                        Start by entering your information in the editor sections, beginning with{" "}
                        <b className="text-neutral-950">Personal Details</b>. Your résumé preview
                        will update automatically as you make changes. If you'd like to begin with a
                        blank résumé at any time, use the{" "}
                        <b className="text-neutral-950">Reset</b> button to clear all content. You
                        can also explore and switch between different résumé designs from the{" "}
                        <b className="text-neutral-950">Templates</b> tab without losing your
                        information.
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCreateGuideVisible(false)}
                      className="text-neutral-400 hover:text-neutral-900 shrink-0"
                      aria-label="Dismiss guidance"
                      data-testid="create-guide-dismiss"
                    >
                      <X size={16} weight="bold" />
                    </button>
                  </div>
                )}
                <ResumeForm resume={resume} setResume={setResume} />
              </>
            )}
          </div>
        </section>

        <PreviewPanel
          previewRef={previewRef}
          resume={resume}
          template={template}
          pageSize={pageSize}
          density={density}
          fontScale={fontScale}
          fontFamily={fontFamily}
          descriptionFontFamily={descriptionFontFamily}
          lineSpacing={lineSpacing}
          h1FontWeight={h1FontWeight}
          h2FontWeight={h2FontWeight}
          bodyFontWeight={bodyFontWeight}
          h1Color={h1Color}
          h2Color={h2Color}
          jobTitleFontSize={jobTitleFontSize}
          jobTitleFontWeight={jobTitleFontWeight}
          jobTitleColor={jobTitleColor}
          jobTitleFontFamily={jobTitleFontFamily}
          companyFontSize={companyFontSize}
          companyFontWeight={companyFontWeight}
          companyColor={companyColor}
          companyFontFamily={companyFontFamily}
          resumeNameFontSize={resumeNameFontSize}
          resumeNameFontFamily={resumeNameFontFamily}
          sectionHeaderFontSize={sectionHeaderFontSize}
          sectionHeaderFontFamily={sectionHeaderFontFamily}
          descriptionFontSize={descriptionFontSize}
          descriptionFontWeight={descriptionFontWeight}
          descriptionColor={descriptionColor}
          contactInfoFontSize={contactInfoFontSize}
          contactInfoFontWeight={contactInfoFontWeight}
          contactInfoColor={contactInfoColor}
          contactInfoFontFamily={contactInfoFontFamily}
          workDescriptionFontSize={workDescriptionFontSize}
          workDescriptionFontWeight={workDescriptionFontWeight}
          workDescriptionColor={workDescriptionColor}
          workDescriptionFontFamily={workDescriptionFontFamily}
          dateFontSize={dateFontSize}
          dateFontWeight={dateFontWeight}
          dateColor={dateColor}
          dateFontFamily={dateFontFamily}
          locationFontSize={locationFontSize}
          locationFontWeight={locationFontWeight}
          locationColor={locationColor}
          locationFontFamily={locationFontFamily}
          bodyTypography={bodyTypography}
          previewZoom={previewZoom}
          setPreviewZoom={setPreviewZoom}
          paidClean={paidClean}
          payCfg={{ ...payCfg, enabled: effectivePayEnabled }}
          localCurrency={localCurrency}
          onMovedSectionsChange={setMovedSections}
          onOverflowChange={setHasOverflow}
          hasContent={hasContent}
          downloading={downloading}
          downloadBlocked={downloadBlocked}
          paidDownloadsLeft={paidDownloadsLeft}
          paidQuotaExhausted={paidQuotaExhausted}
          paidDownloadLimit={PAID_DOWNLOAD_LIMIT}
          onCleanDownload={runDownload}
          onPaidDownload={requestPaidDownload}
          mobileView={mobileView}
        />
      </div>

      {jdOpen && (
        <JdModal
          jd={jd}
          setJd={setJd}
          tailoring={tailoring}
          onSubmit={runTailor}
          onClose={() => setJdOpen(false)}
        />
      )}

      {showPrivacyAlert && <PrivacyAlert onDismiss={dismissPrivacyAlert} />}

      <MobileAdvisory open={mobileAdvisoryOpen} onClose={dismissMobileAdvisory} />

      <OnboardingTour open={tourOpen} onClose={() => setTourOpen(false)} />

      <AlertDialog open={confirmFreeOpen} onOpenChange={setConfirmFreeOpen}>
        <AlertDialogContent data-testid="confirm-free-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">
              Free download · {freeDownloadsLeft} of {freeDownloadLimit} remaining
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed text-neutral-700 space-y-2 pt-1">
              <span className="block">
                You're about to download this résumé for <b className="text-neutral-950">free</b> using
                the {template === "modern" ? "Modern" : "ATS Plain"} template.
              </span>
              <span className="block">
                Free downloads are limited to{" "}
                <b className="text-neutral-950">{freeDownloadLimit} per browser session</b>. After this
                download you will have{" "}
                <b className={freeDownloadsLeft - 1 <= 0 ? "text-[#FF2A00]" : "text-neutral-950"}>
                  {Math.max(0, freeDownloadsLeft - 1)}
                </b>{" "}
                free download{freeDownloadsLeft - 1 === 1 ? "" : "s"} left.
              </span>
              <span className="block text-neutral-600">
                Need more downloads? Switch to any premium template for an unlimited paid download
                bundle, or refresh a new session to reset the counter.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-free-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={runFreeDownload}
              className="bg-neutral-950 text-white hover:bg-neutral-800"
              data-testid="confirm-free-continue"
            >
              Download PDF
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmPayOpen} onOpenChange={setConfirmPayOpen}>
        <AlertDialogContent data-testid="confirm-payment-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">
              Confirm your résumé before payment
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed text-neutral-700 space-y-2 pt-1">
              <span className="block">
                Please review your résumé carefully — content, formatting, and personal details —
                before proceeding to payment.
              </span>
              <span className="block">
                A one-time charge of{" "}
                {discount ? (
                  <>
                    <span className="line-through text-neutral-500">USD {payCfg.amount_usd}</span>{" "}
                    <b className="text-neutral-950">USD {discount.final_usd.toFixed(2)}</b>{" "}
                    <span className="text-[#FF2A00] font-mono text-xs font-bold">({discount.percent}% off · {discount.label})</span>
                  </>
                ) : (
                  <b className="text-neutral-950">USD {payCfg.amount_usd}</b>
                )}{" "}
                unlocks the watermark-free PDF for up to{" "}
                <b className="text-neutral-950">{PAID_DOWNLOAD_LIMIT}</b> downloads in this session.
              </span>
              <span className="block text-neutral-600">
                Payments are final and non-refundable, except in the event of a verifiable
                technical failure at the payment gateway.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-payment-cancel">
              Review résumé
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmAndPay}
              className="bg-neutral-950 text-white hover:bg-neutral-800"
              data-testid="confirm-payment-continue"
            >
              Continue to payment
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset-all confirmation. Destructive — wipes both typography knobs
          and the resume content back to the default software-engineer
          sample. Two-step confirmation keeps accidental data loss impossible. */}
      <AlertDialog open={resetAllOpen} onOpenChange={setResetAllOpen}>
        <AlertDialogContent data-testid="reset-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">
              Reset everything to defaults?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed text-neutral-700 space-y-2 pt-1">
              <span className="block">
                This will restore <b className="text-neutral-950">all typography settings</b>{" "}
                — fonts, sizes, weights, colours, line spacing, and the Work
                Experience Job Title / Company Name overrides — back to their
                defaults.
              </span>
              <span className="block">
                Your <b className="text-neutral-950">résumé content</b>{" "}
                (personal details, experience, education, skills, etc.) will
                also be replaced with the built-in sample. Anything you have
                typed will be lost.
              </span>
              <span className="block text-[#FF2A00] font-medium">
                This action cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-all-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetAll}
              className="bg-[#FF2A00] text-white hover:bg-[#FF2A00]/90"
              data-testid="reset-all-confirm"
            >
              Yes, reset everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Welcome dialog — auto-opens once per browser session on Builder
          mount. Offers a fast path (upload existing résumé) and a clean
          slate (start from scratch = just close). Dismissible via close
          button, Escape key, or clicking outside. */}
      <AlertDialog open={welcomeOpen} onOpenChange={(o) => { if (!o) dismissWelcomeDialog(); else setWelcomeOpen(true); }}>
        <AlertDialogContent className="max-w-lg" data-testid="welcome-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight text-2xl">
              Welcome to Your Résumé Builder
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed text-neutral-700 pt-1">
              Choose how you'd like to get started. Upload an existing résumé to save time, or create a new résumé from scratch.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="mt-2 space-y-3">
            {/* Primary — Upload */}
            <div className="border-2 border-[#2400FF] rounded-md p-4 bg-[#2400FF]/[0.03]">
              <div className="font-medium text-neutral-950 mb-1">Upload an Existing Résumé</div>
              <div className="text-[13px] text-neutral-600 leading-relaxed mb-3">
                Upload your current résumé to auto-extract your information and continue editing with one of our professionally designed templates.
              </div>
              <button
                type="button"
                onClick={() => {
                  dismissWelcomeDialog();
                  setTimeout(() => fileRef.current?.click(), 60);
                }}
                className="w-full h-10 bg-[#2400FF] text-white text-sm font-medium hover:bg-[#2400FF]/90 transition-colors rounded-sm"
                data-testid="welcome-upload-button"
              >
                Upload Résumé
              </button>
            </div>

            {/* Secondary — Start Fresh */}
            <div className="border border-black/10 rounded-md p-4">
              <div className="font-medium text-neutral-950 mb-1">Start from Scratch</div>
              <div className="text-[13px] text-neutral-600 leading-relaxed mb-3">
                Create a new résumé by entering your information step by step and customize it to match your career goals.
              </div>
              <button
                type="button"
                onClick={handleWelcomeCreateNew}
                className="w-full h-10 border border-black/20 text-neutral-950 text-sm font-medium hover:bg-neutral-100 transition-colors rounded-sm"
                data-testid="welcome-create-button"
              >
                Create New Résumé
              </button>
            </div>
          </div>

          <div className="mt-2 text-[11px] text-neutral-500 text-center">
            You can upload your résumé later at any time from the editor.
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sponsored ad slot — lives at the bottom of the Builder page so it
          never competes with editing/preview. Placeholder in dev; live
          AdSense once REACT_APP_ADSENSE_PUBLISHER_ID + _SLOT are set. */}
      <BuilderAdBanner />
    </div>
  );
}
